import { useLayoutEffect, useRef, type FC, } from 'react';
import { type DataGraph } from '@/models/dataGraph.ts';
import { type Connection } from '@/models/connection.ts';
import { OrgChart, type Layout } from 'd3-org-chart';
import * as d3 from 'd3'


export interface OrgChartComponentProps {
  data: DataGraph[];
  connections: Connection[];
  showConnection: boolean;
  currentEnvironment: string;
  onNodeClick: (d: any) => void;
	depthLimit: number;
};


const OrgChartComponent: FC<OrgChartComponentProps> = ({ data, connections, showConnection, currentEnvironment, onNodeClick, depthLimit }: OrgChartComponentProps) => {

  const d3Container = useRef(null);
  let chart: OrgChart<DataGraph & { _highlighted: boolean; _upToTheRootHighlighted: boolean; }> | null = null;

  const updateWindow = (): void => {
    chart?.svgWidth(window.innerWidth - 40)
      .svgHeight(window.innerHeight - 330)
      .render().fit();
  }
  window.onresize = updateWindow;
  // We need to manipulate DOM
  useLayoutEffect(() => {
    if (data && d3Container.current) {
      if (!chart) {
        chart = new OrgChart();
      }
      chart.initialExpandLevel(depthLimit).nodeHeight((d) => {
        if (d.data.dataType?.startsWith('Azure')) {
          return 100;
        }
        return 125 + 25 + 10;
      })
        .nodeWidth((d) => {
          if (d.data.dataType?.startsWith('Azure')) {
            return 450;
          }
          return 300;
        })
        .svgWidth(window.innerWidth - 40)
        .svgHeight(window.innerHeight - 330)
        .childrenMargin(() => 50)
        .compactMarginBetween(() => 35)
        .compactMarginPair(() => 30)
        .neighbourMargin(() => 20)
        .linkUpdate(function (d) {
          d3.select(this)
            .attr('stroke', d => d.data._upToTheRootHighlighted ? '#152785' : '#aaa')

          if (d.data._upToTheRootHighlighted) {
            d3.select(this).raise()
          }
        })
        .nodeContent(function (d, i, arr, state) {
          const color = '#FFFFFF';
          let imageDiffVert = 15 + 2;
          const links = d.data.links;


          let height = d.height;
          const degraded = d.data.status === 'Degraded';
          const unhealthy = d.data.status === 'Unhealthy';
          const unknown = d.data.status === null || d.data.status === 'Unknow' || d.data.status === 'Undefined';

          let organizationalUnit = d.data.service.apiDeclaration?.organizationalUnit;
          if (organizationalUnit !== null && organizationalUnit !== undefined) {
            organizationalUnit = organizationalUnit.split('/').slice(1).join('/');
          }
          else {
            organizationalUnit = d.data.service.type;
          }

          let healthColor = degraded ? '#FFE2C2' : unhealthy ? '#FFCECE' : unknown ? (d.type == 'api' || d.data.status == null ? '#ddd' : ' #f1faff') : '#DBF8DB';
          //chart.setUpToTheRootHighlighted(d.data.id).render().fit();
          if (d.data.dataType == 'Azure APIM LAN' || d.data.dataType?.indexOf('Azure') == 0) {
            height = 100;
            imageDiffVert = 15 + 2;
          }
          return d.data.dataType == 'Azure APIM LAN' ? `
          <div style='width:${d.width
            }px;height:${height}px;padding-top:${imageDiffVert - 2}px;padding-left:1px;padding-right:1px;'>
                          <div style="font-family: 'Inter', sans-serif;margin-left:-1px;width:${d.width - 2}px;height:${height - imageDiffVert + 7}px;border-radius:10px;; background-color: ${healthColor}; border: ${d.data.unknown ? '1px solid #aaa' : d.data.degraded ? '3px solid #ff8400' : d.data.unhealthy ? '3px solid #ff3333' : '1px solid #228B22'}" >
                            <div style="position: absolute; top: 38px;background-color:${color};margin-top:${-imageDiffVert - 25}px;margin-left:${15}px;border-radius:100px;width:40px;height:40px;" ></div>
                            <div style="position: absolute;top: 12px;"><img src=" ${d.data.type}" style="    margin-top: -10px;margin-left:${20}px;border-radius:10px;width:30px;height:30px;" /></div>
                              <div style="font-size:15px;color:#08011E;margin-left:20px;margin-top:25px">APIM LAN</div>
                              <div style="color:${degraded ? '#FF8C00' : unhealthy ? '#ff3333' : unknown ? '#333' : '#228B22'};margin-left:20px;margin-top:3px;font-size:10px;font-weight:bold">${d.data.unknown ? "" : d.data.status
            } </div>
                          </div>
                      </div>` : `
                  <div style='width:${d.width
          }px;height:${height - 10}px;padding-top:${imageDiffVert - 2}px;padding-left:6px;padding-right:6px;margin-bottom: 15px;'>
                          <div style="font-family: 'Inter', sans-serif;margin-left:-1px;width:${d.width - 10}px;height:${height - 10 - imageDiffVert + 7}px;border-radius:10px;; background-color: ${healthColor}; border: ${d.data.unknown ? '1px solid #aaa' : d.data.degraded ? '3px solid #ff8400' : d.data.unhealthy ? '3px solid #ff3333' : '1px solid #228B22'}" >
                            <div style="position: absolute; top: 38px; background-color:${color};margin-top:${-imageDiffVert - 25}px;margin-left:${15}px;border-radius:100px;width:40px;height:40px;" ></div>
                            <div style="position: absolute;top: 12px;">
                              <img src=" ${d.data.type}" style="    margin-top: -10px;margin-left:${20}px;border-radius:10px;width:30px;height:30px;" />
                              ${d.data.problems != "" ? "<img src=\"/chart/warning.png\" style=\"margin-top: -10px;margin-left:${5}px;border-radius:10px;width:30px;height:30px;\" title=\"" + d.data.problems + "\" />" : ""}
                              ${d.data.lifecycle != '' && d.data.lifecycle != undefined && d.data.lifecycle?.toLocaleLowerCase() != "released" ? "<img src=\"/chart/upgrade.png\" style=\"margin-top: -10px;margin-left:${5}px;border-radius:10px;width:30px;height:30px;\" title=\"Api is not released. Check if the lifecycle is correct or if you should use another version or another Api.\" />" : ""}
                            </div>
                              <div style="position: absolute;top: 4px;right: 12px;background: white;padding: 3px;border-radius: 5px;border: 1px solid #ccc;">${organizationalUnit}</div>
                              <div style="font-size:15px;color:#08011E;margin-left:20px;margin-top:25px" class="text-truncate" title="[${d.data.service.name}]: ${d.data.service.apiDeclaration?.name ?? d.data.service.name}">  ${d.data.service.apiDeclaration?.name ?? d.data.service.name
          } </div>
                              <div style="color:${degraded ? '#FF8C00' : unhealthy ? '#ff3333' : unknown ? '#228BFF' : '#228B22'};margin-left:20px;margin-top:3px;font-size:10px;font-weight:bold" title="${d.data.unknown && d.data.service.type.indexOf("Azure") < 0 && d.data.service?.problem != null ? d.data.service?.problem[0]?.detail : ""}">${d.data.unknown ? d.data.service.type.indexOf("Azure") >= 0 ? "" : "Undefined Health status" : d.data.status
          } </div>
                              <div style="color:#228B22;margin-left:20px;margin-top:3px;font-size:10px;font-weight:bold;line-height:30px;height:30px;">
                              ${d.data.checks?.map((check, index) => {
            if (index < 7)
              return `<img src="${check.icon}" style="margin-left:${10}px;border-radius:10px;width:30px;height:30px; display:'inline'}" title=${check.name} />`
            if (index == 7)
              return '<span>... and more</span>'
          }).join('')}
                              ${d.data.nodes?.map(node => {
            return `<img src="/chart/node_${node}.svg" style="margin-left:${10}px;border-radius:10px;width:30px;height:30px; display:${node === 'undefined' ? 'none' : 'inline'}" />`
          }).join('')}
                              </div>
                              <div style="color:#228B22;margin-top:3px;font-size:10px;font-weight:bold">
                              <table style="border-top: 1px solid #E4E2E9; width: 100%; " >
                              <tr>
                                  ${links?.filter(f => f.environment == "all" || f.environment == currentEnvironment).map((item: any, index: any) => {
            return `<td><a href="${item.link}" target='_blank'><img src="/chart/${item.icon}" style="width:30px;height:30px;padding-top: 4px;" class="link" title="${item.name}" /></a></td>`
          }).join('')}
                              </tr>
                              </table>
                              </div>
  
                          </div>
                      </div>
                              `;
        })
          .onNodeClick((d) => {
            console.log(d, 'Id of clicked node ');
            onNodeClick(d);
          })
          .linkGroupArc(function linkValue({ source, target }) {
            const linkGenerationFunc = d3
              .linkHorizontal<any, any, { x: number; y: number }>()
              .x((d) => d.x)
              .y((d) => d.y);
  
            return linkGenerationFunc({
              source: { x: source.x, y: source.y + 155 },
              target: { x: target.x, y: target.y - 135 },
            });
          });
          
      if (showConnection) {
        chart.connections(connections.map((connection) => ({ 'from': connection.from, 'to': connection.to, 'label': "" })));
      }
      chart.layout(window.localStorage.getItem('ChangeLayout') as Layout || 'top')
        .container(d3Container.current)
        .data(data).render().fit();

      //   .container(d3Container.current)
      //   .data(data)
      //   .onNodeClick((d) => {
      //     console.log(d, 'Id of clicked node ');
      //     onNodeClick(d);
      //   })
      //   .linkGroupArc(function linkValue({ source, target }) {
      //     const linkGenerationFunc = d3
      //       .linkHorizontal<any, any, { x: number; y: number }>()
      //       .x((d) => d.x)
      //       .y((d) => d.y);

      //     return linkGenerationFunc({
      //       source: { x: source.x, y: source.y + 155 },
      //       target: { x: target.x, y: target.y - 135 },
      //     });
      //   })
      //   .layout(window.localStorage.getItem('ChangeLayout') as Layout || 'top')
      //   .fit();
      // if (showConnection) {
      //   chart.connections(connections.map((connection) => ({ 'from': connection.from, 'to': connection.to, 'label': "" })));
      // }
      
      // chart.expandAll().render();
      window.chart = chart;
    }
  }, [data, d3Container.current, showConnection]);

  return (
    <div >
      <div ref={d3Container} />
    </div>
  );
};

export default OrgChartComponent;